import type { ReactNode } from "react";
import { Container } from "@/shared/components/layout/Container";
import { Section } from "@/shared/components/layout/Section";
import { MarketingFooter } from "@/routes/marketing/MarketingFooter";
import { MarketingHeader } from "@/routes/marketing/MarketingHeader";

/**
 * Shared layout for legal/policy pages (cookie policy, privacy policy, terms of
 * service). Provides the marketing chrome — fixed header + footer in a
 * sticky-footer column so the footer rests at the bottom on short pages — plus
 * the standard title block (brand eyebrow, page title, "Last updated" line).
 * Compose the body from the `Policy*` content primitives below so every policy
 * page shares one typographic structure.
 */
interface PolicyPageProps {
  /** Page title, e.g. "Cookie Policy". Rendered as the `<h1>`. */
  title: string;
  /** Human-readable effective date, e.g. "June 12, 2026". */
  lastUpdated: string;
  children: ReactNode;
}

export function PolicyPage({ title, lastUpdated, children }: PolicyPageProps) {
  return (
    <div className="flex min-h-screen flex-col">
      <MarketingHeader />
      <main className="flex-1">
        <Section spacing="marketing">
          <Container size="5xl">
            <div className="flex flex-col gap-10">
              <div className="flex flex-col gap-4">
                <p className="type-label-sm tracking-wider uppercase text-text-muted">
                  Artisaire Guestbook
                </p>
                <h1 className="type-display-hero text-text">{title}</h1>
                <p className="type-body-base text-text-muted">
                  Last updated: {lastUpdated}
                </p>
              </div>
              <div className="flex max-w-2xl flex-col gap-10">{children}</div>
            </div>
          </Container>
        </Section>
      </main>
      <MarketingFooter />
    </div>
  );
}

/** A top-level policy section: an `<h2>` heading and its stacked content. */
export function PolicySection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <section className="flex flex-col gap-4">
      <h2 className="type-heading-md text-text">{title}</h2>
      {children}
    </section>
  );
}

/** A nested subsection within a `PolicySection` (an `<h3>` heading). */
export function PolicySubsection({
  title,
  children,
}: {
  title: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-3">
      <h3 className="type-heading-sm text-text">{title}</h3>
      {children}
    </div>
  );
}

/** A body paragraph. */
export function PolicyParagraph({ children }: { children: ReactNode }) {
  return <p className="type-body-base text-text-muted">{children}</p>;
}

/** A bulleted list of plain-text items. */
export function PolicyList({ items }: { items: string[] }) {
  return (
    <ul className="flex list-disc flex-col gap-2 pl-5 text-text-muted">
      {items.map((item) => (
        <li key={item} className="type-body-base">
          {item}
        </li>
      ))}
    </ul>
  );
}
